import { AI_AGREEMENT_URL } from '@/consts/aiAgreement';
import type { DataMaster } from '@/features/ap_payees/components/PayeeDetailDrawer/types';
import { useTranslation } from '@/i18n';
import {
  ButtonGroup,
  ButtonV2,
  Caption,
  Form,
  FormCheckbox,
  TextLink,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import styles from '../../../styles.module.scss';
import { Header } from '../Header';
import { ServiceRows } from '../ServiceRows';

const cx = classnames.bind(styles);

export type FormValues = Record<string, boolean>;

type Props = {
  services: DataMaster['apAiServiceSettings'];
  isPending: boolean;
  onSubmit: (values: FormValues) => Promise<void>;
  onCancel: (isDirty: boolean) => void;
  onDirtyChange: (isDirty: boolean) => void;
};

export const Edit: FC<Props> = ({
  services,
  isPending,
  onSubmit,
  onCancel,
  onDirtyChange,
}) => {
  const { t } = useTranslation();
  const defaultValues = useMemo(
    () =>
      Object.fromEntries(
        services.map(({ service_type, is_enabled }) => [
          service_type,
          is_enabled,
        ])
      ),
    [services]
  );
  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'all',
  });
  const { isDirty, isSubmitting } = useFormState({
    control: methods.control,
  });

  const handleCancel = useCallback(() => {
    onCancel(isDirty);
  }, [isDirty, onCancel]);

  useEffect(() => {
    onDirtyChange(isSubmitting ? false : isDirty);
  }, [isDirty, isSubmitting, onDirtyChange]);

  return (
    <Form<FormValues>
      formMethod={methods}
      onSubmit={onSubmit}
      testId='ap-ai-service-settings-edit'
      className={cx(styles['sectionContainer'])}
    >
      <Header>
        <Caption type='alert' textClassName={cx(styles['alertCaption'])}>
          <TextLink href={AI_AGREEMENT_URL} target='_blank'>
            {t(
              'ai_agents_setting_main_setting_confirm_enable_start_service_terms_link'
            )}
          </TextLink>
          {t('ai_agents_setting_payee_detail_terms_confirmation_suffix')}
        </Caption>
        <ButtonGroup direction='horizontal'>
          <ButtonV2
            color='primary'
            isSecondary
            onClick={handleCancel}
            type='button'
            isDisabled={isPending}
            containerClassName={cx(styles['cancelButton'])}
          >
            {t('cancel')}
          </ButtonV2>
          <ButtonV2
            color='primary'
            size='xs'
            type='submit'
            isLoading={isPending}
            isDisabled={isPending}
            buttonContainerClassName={cx(styles['saveButton'])}
          >
            {t('save')}
          </ButtonV2>
        </ButtonGroup>
      </Header>
      <ServiceRows
        services={services}
        renderValue={(service) => (
          <FormCheckbox<FormValues>
            name={service.service_type}
            control={methods.control}
            inputId={`ap-ai-service-${service.service_type}`}
            label={t('use')}
          />
        )}
      />
    </Form>
  );
};

Edit.displayName = 'ApAiServiceSettingsEdit';
